import type { TranslationTable } from "@repo/shared/types";

const EN_GB = {
  layoutPreview: "Layout preview",
  previewDescription: (dashboard: string) => `${dashboard} · updates as you switch and drag`,
  empty: "empty",
  full: "Full",
  previewNote:
    "Sections fill a 3-column grid in order. A widget that does not fit the rest of a row starts the next row, and the gap stays empty.",
  complianceLegend: "Compliance only — hidden for users without it",
};

// All 17 locales (QL-17 plan keeps them); the type makes each one match EN_GB.
export const T: TranslationTable<typeof EN_GB> = {
  en_GB: EN_GB,
  fr_FR: {
    layoutPreview: "Aperçu de la mise en page",
    previewDescription: (dashboard: string) =>
      `${dashboard} · se met à jour lorsque vous activez ou déplacez un widget`,
    empty: "vide",
    full: "Pleine largeur",
    previewNote:
      "Les sections remplissent dans l'ordre une grille à 3 colonnes. Un widget qui ne tient pas dans le reste d'une ligne passe à la ligne suivante, et l'espace restant reste vide.",
    complianceLegend: "Conformité uniquement — masqué pour les utilisateurs sans ce droit",
  },
  es_ES: {
    layoutPreview: "Vista previa del diseño",
    previewDescription: (dashboard: string) => `${dashboard} · se actualiza al activar y arrastrar`,
    empty: "vacío",
    full: "Completo",
    previewNote:
      "Las secciones llenan en orden una cuadrícula de 3 columnas. Un widget que no cabe en el resto de una fila empieza la fila siguiente, y el hueco queda vacío.",
    complianceLegend: "Solo cumplimiento: oculto para usuarios sin este permiso",
  },
  pt_BR: {
    layoutPreview: "Pré-visualização do layout",
    previewDescription: (dashboard: string) =>
      `${dashboard} · atualiza conforme você ativa e arrasta`,
    empty: "vazio",
    full: "Inteiro",
    previewNote:
      "As seções preenchem em ordem uma grade de 3 colunas. Um widget que não cabe no restante de uma linha começa a linha seguinte, e o espaço fica vazio.",
    complianceLegend: "Somente compliance — oculto para usuários sem essa permissão",
  },
  de_DE: {
    layoutPreview: "Layout-Vorschau",
    previewDescription: (dashboard: string) =>
      `${dashboard} · wird beim Umschalten und Ziehen aktualisiert`,
    empty: "leer",
    full: "Volle Breite",
    previewNote:
      "Bereiche füllen der Reihe nach ein Raster mit 3 Spalten. Ein Widget, das nicht in den Rest einer Reihe passt, beginnt die nächste Reihe, und die Lücke bleibt leer.",
    complianceLegend: "Nur Compliance — für Benutzer ohne diese Berechtigung ausgeblendet",
  },
  it_IT: {
    layoutPreview: "Anteprima del layout",
    previewDescription: (dashboard: string) =>
      `${dashboard} · si aggiorna mentre attivi e trascini`,
    empty: "vuoto",
    full: "Intera",
    previewNote:
      "Le sezioni riempiono in ordine una griglia a 3 colonne. Un widget che non entra nel resto di una riga inizia la riga successiva, e lo spazio rimane vuoto.",
    complianceLegend: "Solo compliance — nascosto agli utenti senza questo permesso",
  },
  ar: {
    layoutPreview: "معاينة التخطيط",
    previewDescription: (dashboard: string) => `${dashboard} · تتحدّث أثناء التبديل والسحب`,
    empty: "فارغ",
    full: "كامل",
    previewNote:
      "تملأ الأقسام شبكة من 3 أعمدة بالترتيب. الأداة التي لا تتسع في بقية الصف تبدأ في الصف التالي، وتبقى المساحة المتبقية فارغة.",
    complianceLegend: "للامتثال فقط — مخفية عن المستخدمين الذين ليست لديهم هذه الصلاحية",
  },
  ja_JP: {
    layoutPreview: "レイアウトのプレビュー",
    previewDescription: (dashboard: string) =>
      `${dashboard} · 切り替えやドラッグに合わせて更新されます`,
    empty: "空",
    full: "全幅",
    previewNote:
      "セクションは順番に 3 列グリッドを埋めていきます。行の残りに収まらないウィジェットは次の行から始まり、空いた部分はそのまま残ります。",
    complianceLegend: "コンプライアンスのみ — 権限のないユーザーには非表示",
  },
  hi_IN: {
    layoutPreview: "लेआउट पूर्वावलोकन",
    previewDescription: (dashboard: string) => `${dashboard} · स्विच करने और खींचने के साथ अपडेट होता है`,
    empty: "खाली",
    full: "पूर्ण",
    previewNote:
      "सेक्शन क्रम से 3-कॉलम ग्रिड भरते हैं। जो विजेट पंक्ति के बचे हिस्से में नहीं समाता, वह अगली पंक्ति से शुरू होता है और खाली जगह खाली ही रहती है।",
    complianceLegend: "केवल अनुपालन — बिना अनुमति वाले उपयोगकर्ताओं से छिपा",
  },
  zh_CN: {
    layoutPreview: "布局预览",
    previewDescription: (dashboard: string) => `${dashboard} · 随切换和拖动实时更新`,
    empty: "空",
    full: "整行",
    previewNote:
      "板块按顺序填充三列网格。若小组件放不进当前行的剩余空间，则从下一行开始，留下的空位保持空白。",
    complianceLegend: "仅限合规——无该权限的用户不可见",
  },
  zh_TW: {
    layoutPreview: "版面預覽",
    previewDescription: (dashboard: string) => `${dashboard} · 隨切換與拖曳即時更新`,
    empty: "空白",
    full: "整列",
    previewNote:
      "區塊會依序填入三欄網格。若小工具放不進該列剩餘的空間，會從下一列開始，留下的空位維持空白。",
    complianceLegend: "僅限合規——無此權限的使用者看不到",
  },
  nl_NL: {
    layoutPreview: "Voorbeeld van indeling",
    previewDescription: (dashboard: string) =>
      `${dashboard} · wordt bijgewerkt terwijl je schakelt en sleept`,
    empty: "leeg",
    full: "Volledig",
    previewNote:
      "Secties vullen op volgorde een raster van 3 kolommen. Een widget die niet in de rest van een rij past, begint op de volgende rij en de ruimte blijft leeg.",
    complianceLegend: "Alleen compliance — verborgen voor gebruikers zonder dit recht",
  },
  nb_NO: {
    layoutPreview: "Forhåndsvisning av oppsett",
    previewDescription: (dashboard: string) =>
      `${dashboard} · oppdateres når du slår av og på og drar`,
    empty: "tom",
    full: "Hel",
    previewNote:
      "Seksjonene fyller et rutenett med 3 kolonner i rekkefølge. En widget som ikke får plass i resten av en rad, starter neste rad, og mellomrommet forblir tomt.",
    complianceLegend: "Kun compliance — skjult for brukere uten tilgang",
  },
  sv_SE: {
    layoutPreview: "Förhandsgranskning av layout",
    previewDescription: (dashboard: string) =>
      `${dashboard} · uppdateras när du slår på/av och drar`,
    empty: "tom",
    full: "Hel",
    previewNote:
      "Sektionerna fyller ett rutnät med 3 kolumner i ordning. En widget som inte får plats i resten av en rad börjar på nästa rad, och luckan förblir tom.",
    complianceLegend: "Endast compliance — dold för användare utan behörigheten",
  },
  fi_FI: {
    layoutPreview: "Asettelun esikatselu",
    previewDescription: (dashboard: string) =>
      `${dashboard} · päivittyy, kun kytket widgetejä ja vedät niitä`,
    empty: "tyhjä",
    full: "Koko leveys",
    previewNote:
      "Osiot täyttävät 3 sarakkeen ruudukon järjestyksessä. Widget, joka ei mahdu rivin jäljellä olevaan tilaan, alkaa seuraavalta riviltä, ja väli jää tyhjäksi.",
    complianceLegend: "Vain compliance — piilotettu käyttäjiltä, joilla ei ole oikeutta",
  },
  da_DK: {
    layoutPreview: "Forhåndsvisning af layout",
    previewDescription: (dashboard: string) =>
      `${dashboard} · opdateres, når du slår til og fra og trækker`,
    empty: "tom",
    full: "Hel",
    previewNote:
      "Sektionerne udfylder et gitter med 3 kolonner i rækkefølge. En widget, der ikke passer ind i resten af en række, starter på næste række, og hullet forbliver tomt.",
    complianceLegend: "Kun compliance — skjult for brugere uden rettigheden",
  },
  is_IS: {
    layoutPreview: "Forskoðun uppsetningar",
    previewDescription: (dashboard: string) =>
      `${dashboard} · uppfærist um leið og þú kveikir, slekkur og dregur`,
    empty: "tómt",
    full: "Full breidd",
    previewNote:
      "Hlutarnir fylla hnitanet með 3 dálkum í röð. Græja sem kemst ekki fyrir í því sem eftir er af röð byrjar í næstu röð og bilið stendur autt.",
    complianceLegend: "Aðeins regluvarsla — falið notendum án heimildar",
  },
};
